import { prefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { isDesktopSafari, isFirefox } from '@lemonade-hq/cdk';
import { clamp, inversedLerp, lerp } from '@lemonade-hq/ts-helpers';
/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
import type { RefObject } from 'react';
import * as Render from './canvas-utils';
import { HEIGHT_MULTIPLIER, WIDTH_MULTIPLIER } from './consts';
import { BACKGROUND, MINIMAP, PLANTS } from './sprites';
import type { Coin, Colors, NonNullRef, Point, Sprite, SpriteSheetItem } from './types';
import {
  accelerate,
  easeIn,
  easeInOut,
  gradualChange,
  isIOS,
  isIOS15,
  isIpad,
  mod,
  overlap,
  percentRemaining,
  randomChoice,
  randomInt,
} from './utils';

const STOP_ANIMATION = isIOS && !isIpad();

interface InitParams {
  width: number;
  height: number;
  lanes: number;
  roadWidth: number;
  cameraHeight: number;
  drawDistance: number;
  fieldOfView: number;
  segmentLength: number;
  rumbleLength: number;
  isInGameMode: boolean;
  isMinimapVisible: boolean;
  colors: Colors;
}

export interface Environment extends Omit<InitParams, 'colors'> {
  fps: number;
  step: number;
  centrifugal: number;
  skySpeed: number;
  hillSpeed: number;
  hillOffset: number;
  playerX: number;
  speed: number;
  maxSpeed: number;
  decel: number;
  leftKeyIsDown: boolean;
  rightKeyIsDown: boolean;
  upKeyIsDown: boolean;
  downKeyIsDown: boolean;
  isPaused: boolean;
}

export interface DrivingProps {
  canvas: HTMLCanvasElement;
  colors: Colors;
  images: string[];
  onReady: (images: unknown) => void;
  renderCb?: (currSegment: Segment, env: Environment) => void;
  onCoinCollected?: (coin: Coin, env: Environment) => void;
}

export interface Part {
  world: {
    x: number;
    y: number;
    z: number;
  };
  camera: {
    x: number;
    y: number;
    z: number;
  };
  screen: {
    scale: number;
    x: number;
    y: number;
    w: number;
  };
}

export interface Segment {
  index: number;
  p1: Part;
  p2: Part;
  curve: number;
  sprites: Sprite[];
  coins: Coin[];
  looped: boolean;
  clip: number;
}

const WINDOW_WIDTH = 1920 * WIDTH_MULTIPLIER;
const WINDOW_HEIGHT = 880 * HEIGHT_MULTIPLIER;
const WINDOW_SIZE_RATIO = WINDOW_WIDTH / WINDOW_HEIGHT;
const COIN_Y_OFFSET = -10;
const INITIAL_MAX_SPEED = 3000;
export const GAME_MAX_SPEED = 4000;

export const DEFAULT_ENVIRONMENT_VALUES: Environment = {
  fps: 60,
  step: 1 / 60,
  width: WINDOW_WIDTH,
  height: WINDOW_HEIGHT,
  centrifugal: 0,
  skySpeed: 0.0005,
  hillSpeed: 0.001,
  hillOffset: 0,
  roadWidth: 600,
  segmentLength: 200,
  rumbleLength: 3,
  lanes: 1,
  fieldOfView: 100,
  cameraHeight: 430,
  drawDistance: 350,
  playerX: 0,
  speed: 2500,
  maxSpeed: INITIAL_MAX_SPEED,
  decel: 100,
  isInGameMode: false,
  leftKeyIsDown: false,
  rightKeyIsDown: false,
  upKeyIsDown: false,
  downKeyIsDown: false,
  isMinimapVisible: true,
  isPaused: false,
};

const ROAD = {
  LENGTH: { NONE: 0, SHORT: 25, MEDIUM: 50, LONG: 100 },
  HILL: { NONE: 0, LOW: 20, MEDIUM: 40, HIGH: 60 },
  CURVE: { NONE: 0, SLIGHT: 0.2, EASY: 2 },
};

export class Driving implements DrivingProps {
  private collectedCoins = 0;

  resolution = 0;

  trackLength = 0;

  cameraDepth = 0;

  playerZ = 0;

  position = 0;

  segments: Segment[] = [];

  env: NonNullRef<Environment>;

  canvas: HTMLCanvasElement;

  images: string[];

  onReady: (images: unknown) => void;

  colors: Colors;

  renderCb?: (currSegment: Segment, env: Environment) => void;

  onCoinCollected?: (coin: Coin, env: Environment) => void;

  constructor(props: DrivingProps, env: RefObject<Partial<Environment>> = { current: {} }) {
    this.env = env as NonNullRef<Environment>;

    this.env.current.fps = this.env.current.fps || DEFAULT_ENVIRONMENT_VALUES.fps; // how many 'update' frames per second
    this.env.current.step = this.env.current.step || DEFAULT_ENVIRONMENT_VALUES.step; // how long is each frame (in seconds)
    this.env.current.width = this.env.current.width || DEFAULT_ENVIRONMENT_VALUES.width; // logical canvas width
    this.env.current.height = this.env.current.height || DEFAULT_ENVIRONMENT_VALUES.height; // logical canvas height
    this.env.current.centrifugal = this.env.current.centrifugal || DEFAULT_ENVIRONMENT_VALUES.centrifugal; // centrifugal force multiplier when going around curves
    this.env.current.skySpeed = this.env.current.skySpeed || DEFAULT_ENVIRONMENT_VALUES.skySpeed; // background sky layer scroll speed when going around curve (or up hill)
    this.env.current.hillSpeed = this.env.current.hillSpeed || DEFAULT_ENVIRONMENT_VALUES.hillSpeed; // background hill layer scroll speed when going around curve (or up hill)
    this.env.current.hillOffset = this.env.current.hillOffset || DEFAULT_ENVIRONMENT_VALUES.hillOffset; // current hill scroll offset
    this.env.current.roadWidth = this.env.current.roadWidth || DEFAULT_ENVIRONMENT_VALUES.roadWidth; // actually half the roads width, easier math if the road spans from -roadWidth to +roadWidth
    this.env.current.segmentLength = this.env.current.segmentLength || DEFAULT_ENVIRONMENT_VALUES.segmentLength; // length of a single segment
    this.env.current.rumbleLength = this.env.current.rumbleLength || DEFAULT_ENVIRONMENT_VALUES.rumbleLength; // number of segments per red/white rumble strip
    this.env.current.lanes = this.env.current.lanes || DEFAULT_ENVIRONMENT_VALUES.lanes; // number of lanes
    this.env.current.fieldOfView = this.env.current.fieldOfView || DEFAULT_ENVIRONMENT_VALUES.fieldOfView; // angle (degrees) for field of view
    this.env.current.cameraHeight = this.env.current.cameraHeight || DEFAULT_ENVIRONMENT_VALUES.cameraHeight; // z height of camera
    this.env.current.drawDistance = this.env.current.drawDistance || DEFAULT_ENVIRONMENT_VALUES.drawDistance; // number of segments to draw
    this.env.current.playerX = this.env.current.playerX || DEFAULT_ENVIRONMENT_VALUES.playerX; // player x offset from center of road (-1 to 1 to stay independent of roadWidth)
    this.env.current.speed = this.env.current.speed || DEFAULT_ENVIRONMENT_VALUES.speed; // current speed
    this.env.current.maxSpeed = this.env.current.maxSpeed || DEFAULT_ENVIRONMENT_VALUES.maxSpeed; // top speed (ensure we can't move more than 1 segment in a single frame to make collision detection easier)
    this.env.current.decel = this.env.current.decel || DEFAULT_ENVIRONMENT_VALUES.decel; // 'natural' deceleration rate when neither accelerating, nor braking
    this.env.current.isInGameMode = this.env.current.isInGameMode || DEFAULT_ENVIRONMENT_VALUES.isInGameMode;
    this.env.current.leftKeyIsDown = this.env.current.leftKeyIsDown || DEFAULT_ENVIRONMENT_VALUES.leftKeyIsDown;
    this.env.current.rightKeyIsDown = this.env.current.rightKeyIsDown || DEFAULT_ENVIRONMENT_VALUES.rightKeyIsDown;
    this.env.current.upKeyIsDown = this.env.current.upKeyIsDown || DEFAULT_ENVIRONMENT_VALUES.upKeyIsDown;
    this.env.current.downKeyIsDown = this.env.current.downKeyIsDown || DEFAULT_ENVIRONMENT_VALUES.downKeyIsDown;
    this.env.current.isMinimapVisible =
      this.env.current.isMinimapVisible || DEFAULT_ENVIRONMENT_VALUES.isMinimapVisible;
    this.env.current.isPaused = this.env.current.isPaused || DEFAULT_ENVIRONMENT_VALUES.isPaused;

    this.canvas = props.canvas;
    this.images = props.images;
    this.onReady = props.onReady;
    this.colors = props.colors;
    this.renderCb = props.renderCb;
    this.onCoinCollected = props.onCoinCollected;
  }

  // -----
  // UTILS
  // -----
  private findSegment(index: number): Segment {
    return this.segments[mod(Math.floor(index / this.env.current.segmentLength), this.segments.length)];
  }

  private lastY(): number {
    return this.segments.length === 0 ? 0 : this.segments[this.segments.length - 1].p2.world.y;
  }

  private project(
    p: Part,
    cameraX: number,
    cameraY: number,
    cameraZ: number,
    cameraDepth: number,
    width: number,
    height: number,
    roadWidth: number,
  ): void {
    p.camera.x = (p.world.x || 0) - cameraX;
    p.camera.y = (p.world.y || 0) - cameraY;
    p.camera.z = (p.world.z || 0) - cameraZ;
    p.screen.scale = cameraDepth / p.camera.z;
    p.screen.x = Math.round(width / 2 + (p.screen.scale * p.camera.x * width) / 2);
    p.screen.y = Math.round(height / 2 - (p.screen.scale * p.camera.y * height) / 2);
    p.screen.w = Math.round((p.screen.scale * roadWidth * width) / 2);
  }

  async loadImages(urls: string[]): Promise<HTMLImageElement[]> {
    return new Promise(res => {
      // load multiple images and callback when ALL images have loaded
      const result: HTMLImageElement[] = [];
      let count = urls.length;

      const onload = (): void => {
        count -= 1;

        if (count === 0) res(result);
      };

      for (let n = 0; n < urls.length; n++) {
        result[n] = document.createElement('img');
        result[n].addEventListener('load', onload);
        result[n].src = urls[n];
      }
    });
  }

  // ---------------
  // BUILD GEOMETRY
  // ---------------
  private addSegment(curve: number, y: number): void {
    const n = this.segments.length;
    this.segments.push({
      index: n,
      p1: {
        world: { y: this.lastY(), z: n * this.env.current.segmentLength },
        camera: { y: 0, z: 0 },
        screen: { scale: 1 },
      },
      p2: {
        world: { y, z: (n + 1) * this.env.current.segmentLength },
        camera: { y: 0, z: 0 },
        screen: { scale: 1 },
      },
      curve,
      sprites: [],
      coins: [],
    } as unknown as Segment);
  }

  private addSprite(segmentIndex: number, sprite: SpriteSheetItem, offset: number): void {
    this.segments[segmentIndex].sprites.push({
      source: sprite,
      offset,
      scale: lerp(0.8, 1.1, Math.random()),
    });
  }

  private addRoad(enter: number, hold: number, leave: number, curve: number, y = 0): void {
    const startY = this.lastY();
    const endY = startY + y * this.env.current.segmentLength;
    const total = enter + hold + leave;

    for (let n = 0; n < enter; n++) {
      this.addSegment(easeIn(0, curve, n / enter), easeInOut(startY, endY, n / total));
    }

    for (let n = 0; n < hold; n++) {
      this.addSegment(curve, easeInOut(startY, endY, (enter + n) / total));
    }

    for (let n = 0; n < leave; n++) {
      this.addSegment(easeInOut(curve, 0, n / leave), easeInOut(startY, endY, (enter + hold + n) / total));
    }
  }

  private addHill(num = ROAD.LENGTH.MEDIUM, height = ROAD.HILL.MEDIUM): void {
    this.addRoad(num, num, num, 0, height);
  }

  private addCurve(num = ROAD.LENGTH.SHORT, curve = ROAD.CURVE.EASY, height = ROAD.HILL.NONE): void {
    this.addRoad(num, num, num, curve, height);
  }

  // -----------
  // RESET WORLD
  // -----------
  private resetSprites(): void {
    // this.addSprite(this.env.current.drawDistance, BILLBOARDS[0], -3);

    // Add trees
    for (let n = 0; n < this.segments.length; n += 3) {
      this.addSprite(n, randomChoice(PLANTS), randomChoice([1, -1]) * (2 + Math.random() * 30));
    }
  }

  private resetCoins(): void {
    for (let n = 0; n < this.segments.length; n += randomInt(15, 40)) {
      this.segments[n].coins.push({
        type: 'point',
        collected: false,
        moveDirection: 0,
        z: n,
        offset: lerp(-1, 1, Math.random()),
        mega: n % 3 === 0 && Math.random() < 0.3,
      });
    }

    for (let n = 0; n < this.segments.length; n += randomInt(60, 100)) {
      this.segments[n].coins.push({
        type: 'time',
        collected: false,
        moveDirection: 0,
        z: n,
        offset: lerp(-1, 1, Math.random()),
        mega: n % 3 === 0 && Math.random() < 0.3,
      });
    }
  }

  private resetRoad(): void {
    this.segments = [];

    this.addCurve(30, 0, 3);
    this.addCurve(ROAD.LENGTH.SHORT, -0.2, 3);
    this.addCurve(ROAD.LENGTH.SHORT, 0.2, -3);
    this.addCurve(ROAD.LENGTH.SHORT, 0.2, 3);
    this.addCurve(ROAD.LENGTH.SHORT, -0.2, -3);
    this.addCurve(ROAD.LENGTH.SHORT, -ROAD.CURVE.EASY, ROAD.HILL.NONE);
    this.addCurve(ROAD.LENGTH.SHORT, ROAD.CURVE.EASY, 10);
    this.addHill(ROAD.LENGTH.SHORT, -5);
    this.addCurve(ROAD.LENGTH.SHORT, 0.2, -3);
    this.addCurve(ROAD.LENGTH.SHORT, -0.2, 3);
    this.addCurve(ROAD.LENGTH.SHORT, 0.2, 3);
    this.addCurve(ROAD.LENGTH.SHORT, -0.2, -3);
    this.addCurve(ROAD.LENGTH.SHORT, ROAD.CURVE.EASY, -5);
    this.addCurve(ROAD.LENGTH.SHORT, -ROAD.CURVE.EASY, ROAD.HILL.NONE);
    this.addCurve(30, 0, -3);

    this.resetSprites();
    this.resetCoins();

    this.trackLength = this.segments.length * this.env.current.segmentLength;
  }

  reset(canvas: HTMLCanvasElement, options: Partial<InitParams> = {}): void {
    const DEVICE_PIXEL_RATIO = window.devicePixelRatio;
    // Set display size (css pixels).
    const width = options.width || this.env.current.width;
    const height = width / WINDOW_SIZE_RATIO;
    const ctx = canvas.getContext('2d', isIOS || isDesktopSafari() ? { alpha: false } : {});

    canvas.width = Math.floor(width * DEVICE_PIXEL_RATIO);
    canvas.height = Math.floor(height * DEVICE_PIXEL_RATIO);
    ctx?.scale(DEVICE_PIXEL_RATIO, DEVICE_PIXEL_RATIO);

    this.env.current.width = width;
    this.env.current.height = height;
    this.env.current.lanes = options.lanes || this.env.current.lanes;
    this.env.current.roadWidth = options.roadWidth || this.env.current.roadWidth;
    this.env.current.cameraHeight = options.cameraHeight || this.env.current.cameraHeight;
    this.env.current.drawDistance = options.drawDistance || this.env.current.drawDistance;
    this.env.current.fieldOfView = options.fieldOfView || this.env.current.fieldOfView;
    this.env.current.segmentLength = options.segmentLength || this.env.current.segmentLength;
    this.env.current.rumbleLength = options.rumbleLength || this.env.current.rumbleLength;

    this.cameraDepth = 1 / Math.tan(((this.env.current.fieldOfView / 2) * Math.PI) / 180);
    this.playerZ = this.env.current.cameraHeight * this.cameraDepth;
    this.resolution = height / 480;

    if (this.segments.length === 0 || options.segmentLength || options.rumbleLength) {
      this.resetRoad(); // only rebuild road when necessary
    }
  }

  // --------------------
  // UPDATE DRIVING WORLD
  // --------------------
  private updateSpeed(dt: number, playerSegment: Segment): void {
    if (this.env.current.isInGameMode) {
      if (this.env.current.upKeyIsDown) {
        this.env.current.speed = accelerate(this.env.current.speed, this.env.current.decel * 2, dt);
      } else if (this.env.current.downKeyIsDown) {
        this.env.current.speed = accelerate(this.env.current.speed, -this.env.current.decel * 3, dt);
      }
    } else if (
      playerSegment.curve > 0.5 ||
      playerSegment.curve < -0.5 ||
      Math.random() > 0.55 ||
      this.env.current.speed > INITIAL_MAX_SPEED
    ) {
      this.env.current.speed = accelerate(this.env.current.speed, -this.env.current.decel, dt);
    } else {
      this.env.current.speed = accelerate(this.env.current.speed, this.env.current.decel * 2, dt);
    }

    this.env.current.speed = clamp(this.env.current.speed, 0, this.env.current.maxSpeed); // or exceed maxSpeed
  }

  private updateCoins(_: number, playerSegment: Segment): void {
    const baseSegment = this.findSegment(this.position);

    for (let n = 0; n < this.env.current.drawDistance; n++) {
      const segment = this.segments[(baseSegment.index + n) % this.segments.length];

      for (let i = 0; i < segment.coins.length; i++) {
        const coin = segment.coins[i];

        // make sure used coins can be recollected
        if (n > 10) {
          coin.collected = false;
        }

        if (this.collectedCoins > 10) {
          const div = clamp(this.collectedCoins / 3000, 0, 0.1);

          if (coin.moveDirection === 0) coin.moveDirection = randomChoice([-1, 1]);
          else if (coin.moveDirection === 1) {
            if (coin.offset < 1) coin.offset += div;
            else coin.moveDirection = -1;
          } else if (coin.moveDirection === -1) {
            if (coin.offset > -1) coin.offset -= div;
            else coin.moveDirection = 1;
          }
        }
      }
    }

    for (let n = 0; n < playerSegment.coins.length; n++) {
      const coin = playerSegment.coins[n];

      if (!coin.collected && overlap(this.env.current.playerX, 0.5, coin.offset, 0.2, 0.7)) {
        coin.collected = true;
        this.onCoinCollected?.(coin, this.env.current);

        if (coin.type === 'point') this.collectedCoins += 1;
        break;
      }
    }
  }

  private updatePlayerX(dt: number, playerSegment: Segment): void {
    const speedPercent = this.env.current.speed / this.env.current.maxSpeed;
    const dx = dt * 2 * speedPercent; // at top speed, should be able to cross from left to right (-1 to 1) in 1 second

    this.env.current.playerX -= dx * speedPercent * playerSegment.curve * this.env.current.centrifugal;

    if (this.env.current.isInGameMode) {
      if (this.env.current.leftKeyIsDown) this.env.current.playerX -= dx;
      else if (this.env.current.rightKeyIsDown) this.env.current.playerX += dx;
    } else if (this.env.current.playerX !== 0) {
      // Slide back to the center after a game
      if (this.env.current.playerX < 0) {
        this.env.current.playerX += dx / 2;
      } else if (this.env.current.playerX > 0) {
        this.env.current.playerX -= dx / 2;
      }

      if (this.env.current.playerX < 0.01 && this.env.current.playerX > -0.01) this.env.current.playerX = 0;
    }

    this.env.current.playerX = clamp(this.env.current.playerX, -1, 1); // don't ever let it go too far out of bounds
  }

  private update(dt: number): void {
    const playerSegment = this.findSegment(this.position + this.playerZ);
    const startPosition = this.position;

    this.position = gradualChange(this.position, dt * this.env.current.speed, this.trackLength);

    this.updateSpeed(dt, playerSegment);
    this.updatePlayerX(dt, playerSegment);

    if (this.env.current.isInGameMode) {
      this.updateCoins(dt, playerSegment);
    }

    this.env.current.hillOffset = gradualChange(
      this.env.current.hillOffset,
      (this.env.current.hillSpeed * playerSegment.curve * (this.position - startPosition)) /
        this.env.current.segmentLength,
      1,
    );
  }

  // ------
  // RENDER
  // ------
  private renderRoad(ctx: CanvasRenderingContext2D, baseSegment: Segment, basePercent: number, playerY: number): void {
    let maxY = this.env.current.height;
    let x = 0;
    let dx = -(baseSegment.curve * basePercent);

    // Calc front road & paint
    for (let n = 0; n < this.env.current.drawDistance; n++) {
      const segment = this.segments[mod(baseSegment.index + n, this.segments.length)];
      segment.looped = segment.index < baseSegment.index;
      segment.clip = maxY;

      this.project(
        segment.p1,
        this.env.current.playerX * this.env.current.roadWidth - x,
        playerY + this.env.current.cameraHeight,
        this.position - (segment.looped ? this.trackLength : 0),
        this.cameraDepth,
        this.env.current.width,
        this.env.current.height,
        this.env.current.roadWidth,
      );
      this.project(
        segment.p2,
        this.env.current.playerX * this.env.current.roadWidth - x - dx,
        playerY + this.env.current.cameraHeight,
        this.position - (segment.looped ? this.trackLength : 0),
        this.cameraDepth,
        this.env.current.width,
        this.env.current.height,
        this.env.current.roadWidth,
      );

      x += dx;
      dx += segment.curve;

      if (
        segment.p1.camera.z <= this.cameraDepth || // behind us
        segment.p2.screen.y >= segment.p1.screen.y || // back face cull
        segment.p2.screen.y >= maxY
      )
        // clip by (already rendered) hill
        continue;

      Render.road(
        ctx,
        this.env.current.width,
        segment.p1.screen.x,
        segment.p1.screen.y,
        segment.p1.screen.w,
        segment.p2.screen.x,
        segment.p2.screen.y,
        segment.p2.screen.w,
        this.colors.road,
        this.env.current.lanes,
        Math.floor(segment.index / 3) % 2 === 0,
      );

      maxY = segment.p1.screen.y;
    }
  }

  private renderSprites(ctx: CanvasRenderingContext2D, baseSegment: Segment, sprites: HTMLImageElement): void {
    for (let n = this.env.current.drawDistance - 1; n > 0; n--) {
      const segment = this.segments[(baseSegment.index + n) % this.segments.length];

      // coins
      if (this.env.current.isInGameMode) {
        for (let i = 0; i < segment.coins.length; i++) {
          const coin = segment.coins[i];
          const spriteScale = segment.p1.screen.scale;

          const coinX =
            segment.p1.screen.x + (spriteScale * coin.offset * this.env.current.roadWidth * this.env.current.width) / 2;

          const coinY = segment.p1.screen.y + (spriteScale * (coin.mega ? -200000 : -270000)) / 2 + COIN_Y_OFFSET;
          // const coinY = segment.p1.screen.y;
          // const { DIAMOND, MEGA_DIAMOND, TIME, MEGA_TIME } = COLLECTABLES;

          // const sprite =
          //     coin.type === 'point' ? (coin.mega ? MEGA_DIAMOND : DIAMOND) : coin.mega ? MEGA_TIME : TIME;

          // Render.sprite(
          //     ctx,
          //     this.env.current.width,
          //     this.env.current.roadWidth,
          //     sprites,
          //     sprite,
          //     spriteScale,
          //     coinX,
          //     segment.p1.screen.y,
          //     0,
          //     -0.9,
          //     segment.clip,
          // );

          if (segment.clip > coinY) {
            Render.circle(
              ctx,
              coinX,
              coinY,
              spriteScale * 40000 * (coin.mega ? 2 : 1),
              this.colors.coins[coin.type].color,
              spriteScale * 70000 * (coin.mega ? 4 : 1),
              this.colors.coins[coin.type].border,
              spriteScale * 11000,
            );
          }
        }
      }

      // sprites
      for (let i = 0; i < segment.sprites.length; i++) {
        const sprite = segment.sprites[i];
        const spriteScale = segment.p1.screen.scale * sprite.scale;
        const spriteX =
          segment.p1.screen.x + (spriteScale * sprite.offset * this.env.current.roadWidth * this.env.current.width) / 2;
        const spriteY = segment.p1.screen.y;

        Render.sprite(
          ctx,
          this.env.current.width,
          this.env.current.roadWidth,
          sprites,
          sprite.source,
          spriteScale,
          spriteX,
          spriteY,
          sprite.offset < 0 ? -1 : 0,
          -1,
          segment.clip,
        );
      }
    }
  }

  private renderMinimap(
    ctx: CanvasRenderingContext2D,
    baseSegment: Segment,
    basePercent: number,
    playerY: number,
  ): void {
    if (!this.env.current.isMinimapVisible) return;

    const dy = ((baseSegment?.p1.world.y ?? 0) - (baseSegment?.p2.world.y ?? 0)) / 5;
    Render.roundRect(ctx, MINIMAP.x, MINIMAP.y + dy, MINIMAP.w, MINIMAP.h, 45, this.colors.minimap.background);

    const minimapCenterPoint = 0.65;
    const minimapDistance = Math.round(this.env.current.drawDistance * minimapCenterPoint);

    const minimapXScale = 500;

    // Minimap Front road projection
    let x = 0;
    let dx = -baseSegment.curve * basePercent;
    for (let n = 0; n < minimapDistance; n++) {
      const segment = this.segments[mod(baseSegment.index + n, this.segments.length)];

      this.project(
        segment.p1,
        -x,
        playerY + this.env.current.cameraHeight,
        this.position + n,
        this.cameraDepth,
        this.env.current.width,
        this.env.current.height,
        this.env.current.roadWidth,
      );

      x += dx;
      dx += segment.curve;
    }

    // Minimap Back road projection
    x = 0;
    dx = baseSegment.curve * basePercent;
    for (let n = 0; n < minimapDistance; n++) {
      const segment = this.segments[mod(baseSegment.index - n, this.segments.length)];

      // TODO: fix projection of end of loop segments
      this.project(
        segment.p1,
        -x,
        playerY + this.env.current.cameraHeight,
        this.position - n,
        this.cameraDepth,
        this.env.current.width,
        this.env.current.height,
        this.env.current.roadWidth,
      );

      x += dx;
      dx += segment.curve;
    }

    // Draw minimap
    const points: Point[] = [];
    for (let n = -minimapDistance; n < this.env.current.drawDistance - minimapDistance; n += 3) {
      const segment = this.segments[mod(baseSegment.index + n, this.segments.length)];

      const x = lerp(
        MINIMAP.x - minimapXScale / 2,
        MINIMAP.x + MINIMAP.w + minimapXScale / 2,
        inversedLerp(0, this.env.current.width, segment.p1.screen.x),
      );
      const y = lerp(
        MINIMAP.y + dy + MINIMAP.h,
        MINIMAP.y + dy,
        inversedLerp(-minimapDistance, this.env.current.drawDistance - minimapDistance, n),
      );

      points.push({ x, y });
    }

    Render.line(ctx, points, 10, this.colors.minimap.road);

    Render.line(ctx, points, 2, this.colors.minimap.line, minimapCenterPoint);

    Render.circle(
      ctx,
      lerp(MINIMAP.x, MINIMAP.x + MINIMAP.w, 0.5),
      lerp(MINIMAP.y + dy + MINIMAP.h, MINIMAP.y + dy, minimapCenterPoint),
      5,
      this.colors.minimap.marker,
      20,
      this.colors.minimap.markerBorder,
      1,
    );
  }

  private render(ctx: CanvasRenderingContext2D, background: HTMLImageElement, sprites: HTMLImageElement): void {
    const baseSegment = this.findSegment(this.position);
    const basePercent = percentRemaining(this.position, this.env.current.segmentLength);
    const playerSegment = this.findSegment(this.position + this.playerZ);
    const playerPercent = percentRemaining(this.position + this.playerZ, this.env.current.segmentLength);
    const playerY = lerp(playerSegment.p1.world.y, playerSegment.p2.world.y, playerPercent);

    ctx.clearRect(0, 0, this.env.current.width, this.env.current.height);

    Render.background(
      ctx,
      background,
      this.env.current.width,
      this.env.current.height,
      BACKGROUND.HILLS,
      this.env.current.hillOffset,
      this.resolution * this.env.current.hillSpeed * playerY,
    );

    // Calc front road & paint
    this.renderRoad(ctx, baseSegment, basePercent, playerY);

    // Draw sprites & coins
    this.renderSprites(ctx, baseSegment, sprites);

    // Draw minimap
    this.renderMinimap(ctx, baseSegment, basePercent, playerY);

    // callback to react
    this.renderCb?.(playerSegment, this.env.current);
  }

  // -----------
  // VROOM VROOM
  // -----------
  async run(): Promise<void> {
    const images = await this.loadImages(this.images);

    const [background, sprites] = images;
    this.onReady(images); // tell caller to initialize itself because images are loaded and we're ready to rumble
    this.reset(this.canvas);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    let last = new Date().getTime();
    let dt = 0;
    let gdt = 0;

    function frame(): void {
      const now = new Date().getTime();
      dt = Math.min(1, (now - last) / 1000); // using requestAnimationFrame have to be able to handle large delta's caused when it 'hibernates' in a background or non-visible tab
      gdt += dt;

      while (gdt > self.env.current.step) {
        gdt -= self.env.current.step;
        if (!self.env.current.isPaused) self.update(self.env.current.step);
      }
      const ctx = self.canvas?.getContext('2d', isIOS || isDesktopSafari() ? { alpha: false } : {});

      if (!ctx) return;

      if (!self.env.current.isPaused) self.render(ctx, background, sprites);
      last = now;

      // don't run game loop in firefox
      if (!STOP_ANIMATION && !isFirefox() && !isIOS15() && !prefersReducedMotion()) {
        requestAnimationFrame(frame);
      }
    }

    frame(); // lets get this party started
  }

  pause(): void {
    this.env.current.isPaused = true;
  }

  resume(): void {
    this.env.current.isPaused = false;
  }

  toggleMinimap(force?: boolean): void {
    this.env.current.isMinimapVisible = force != null ? force : !this.env.current.isMinimapVisible;
  }

  startGame(): void {
    this.env.current.isInGameMode = true;
    this.env.current.lanes = 3;
    this.env.current.centrifugal = 0.3;
    this.env.current.maxSpeed = 4000;
  }

  endGame(): number {
    this.env.current.isInGameMode = false;
    this.env.current.lanes = DEFAULT_ENVIRONMENT_VALUES.lanes;
    this.env.current.centrifugal = DEFAULT_ENVIRONMENT_VALUES.centrifugal;
    this.env.current.maxSpeed = DEFAULT_ENVIRONMENT_VALUES.maxSpeed;
    this.env.current.leftKeyIsDown = false;
    this.env.current.rightKeyIsDown = false;
    this.env.current.upKeyIsDown = false;
    this.env.current.downKeyIsDown = false;

    const coins = this.collectedCoins;
    this.collectedCoins = 0;

    return coins;
  }
}
